import React, { type ReactNode } from 'react'

import { Alert, SimpleTable, TableSkeleton } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'
import { formatDurationSeconds } from '@/utils/formatDate'

export interface DoorEventsSummaryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
  applicationType?: string
}

export interface DoorEventStats {
  totalOpenSeconds: number
  totalClosedSeconds: number
  openRatio: number
  averageOpenSeconds: number
  maxOpenSeconds: number
  openings: number
  openingsWithinGeofence: number
}

export interface DoorSummary extends DoorEventStats {
  monitoringPointId: number
  doorName: string
}

export interface DoorEventsSummaryResponse {
  data: DoorSummary[]
  count: number
  anyDoor: DoorEventStats
  from?: string
  to?: string
}

interface DoorEventsSummaryTableRow extends DoorEventStats {
  door: string
  monitoringPointId: number
}

export type DoorEventsSummaryData = DoorEventsSummaryTableRow[] & {
  totals: DoorEventStats
  from?: string
  to?: string
  count: number
}

export const reportPath = '/insights/reports/door-events-summary'

const HEADERS = [
  'Door',
  'Total Time Door Open',
  'Total Time Door Closed',
  'Opened to Closed Ratio',
  'Average Open Time',
  'Maximum Open Time',
  'Total Door Openings',
  'Total Door Openings within Geofence',
]

export const flattenDoorEventsSummary = (response: DoorEventsSummaryResponse): DoorEventsSummaryData => {
  const rows = response.data.map((door) => ({
    ...door,
    door: door.doorName,
  }))

  return Object.assign(rows, {
    totals: response.anyDoor,
    ...(response.from && { from: response.from }),
    ...(response.to && { to: response.to }),
    count: response.count,
  })
}

const timeDisplay = (seconds: number): string => formatDurationSeconds(seconds)

const ratioDisplay = (ratio: number): string => `${(ratio * 100).toFixed(2)}%`

const DoorEventsSummary: InsightDefinition<DoorEventsSummaryParams, DoorEventsSummaryData> = {
  active: true,
  slug: 'report/door-events-summary',
  category: 'report',
  name: 'Door Events Summary',
  description: 'Summaries door open and closed activity for selected assets',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      if (field === 'companyId') {
        return { ...parameters, assetId: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'assetId',
        control: 'AssetDropdownSingle',
        required: true,
        assetType: AssetTypeFilter.InTransit,
      },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom.map((option) =>
          option.value === 'today' ? { ...option, isDefault: true } : option
        ),
      },
      {
        id: 'dateRange',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (parameters: DoorEventsSummaryParams) => {
      if (!parameters.companyId || !parameters.assetId) {
        return 'Company and Asset are required.'
      }

      if (parameters.date === 'custom') {
        if (!parameters.from || !parameters.to) {
          return 'Enter the custom date range.'
        }
        const fromDate = new Date(parameters.from)
        const toDate = new Date(parameters.to)
        if (fromDate > toDate) {
          return 'Start date must be before end date'
        }
        const dateValidation = validateDateRangeWithin13Months(fromDate.toISOString(), toDate.toISOString())
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: DoorEventsSummaryParams) => {
    try {
      const response = await api.simpleGet<DoorEventsSummaryResponse>({
        path: `${reportPath}?${buildQuery({ ...parameters, applicationType: 'inTransit' })}`,
        origin: 'DoorEventsSummary',
      })
      if (!response.data?.length) {
        return { code: InsightStatusCode.NO_DATA }
      }
      const data = flattenDoorEventsSummary(response)
      return data.length ? data : { code: InsightStatusCode.NO_DATA }
    } catch (error) {
      return {
        code: InsightStatusCode.ERROR,
        message: error instanceof Error ? error.message : 'Unable to load door events summary data',
      }
    }
  },

  csv: async (api: ApiService, parameters: DoorEventsSummaryParams, title: string) => {
    await downloadCsv(api, reportPath, { ...parameters, applicationType: 'inTransit' }, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={HEADERS.length} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data) || !data?.length) {
      return null
    }

    const body: Record<string, ReactNode>[] = data.map((row) => ({
      Door: row.door,
      'Total Time Door Open': timeDisplay(row.totalOpenSeconds),
      'Total Time Door Closed': timeDisplay(row.totalClosedSeconds),
      'Opened to Closed Ratio': ratioDisplay(row.openRatio),
      'Average Open Time': timeDisplay(row.averageOpenSeconds),
      'Maximum Open Time': timeDisplay(row.maxOpenSeconds),
      'Total Door Openings': row.openings,
      'Total Door Openings within Geofence': row.openingsWithinGeofence,
    }))
    const totalOpenSeconds = data.reduce((total, row) => total + row.totalOpenSeconds, 0)
    body.push({
      Door: '',
      'Total Time Door Open': <strong>{timeDisplay(totalOpenSeconds)}</strong>,
      'Total Time Door Closed': '',
      'Opened to Closed Ratio': '',
      'Average Open Time': '',
      'Maximum Open Time': '',
      'Total Door Openings': '',
      'Total Door Openings within Geofence': '',
    })

    const totals = data.totals
    body.push({
      Door: <strong>Any Door</strong>,
      'Total Time Door Open': <strong>{timeDisplay(totals.totalOpenSeconds)}</strong>,
      'Total Time Door Closed': <strong>{timeDisplay(totals.totalClosedSeconds)}</strong>,
      'Opened to Closed Ratio': <strong>{ratioDisplay(totals.openRatio)}</strong>,
      'Average Open Time': <strong>{timeDisplay(totals.averageOpenSeconds)}</strong>,
      'Maximum Open Time': <strong>{timeDisplay(totals.maxOpenSeconds)}</strong>,
      'Total Door Openings': <strong>{totals.openings}</strong>, //change if this is the count in the response or the openings
      'Total Door Openings within Geofence': <strong>{totals.openingsWithinGeofence}</strong>,
    })

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={HEADERS} body={body} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status, data }) => {
    const { companies } = useCompany()
    const parameters = configuration.parameters as DoorEventsSummaryParams
    if (status.code === InsightStatusCode.ERROR) return ''

    const fields: Record<string, string> = {}
    const company = companies.find((item) => item.id === parameters.companyId)
    if (company) fields.Company = company.name
    if (parameters.assetId) fields.Asset = parameters.assetId

    if (data?.from && data.to) {
      addDateToCaptionFields({ from: data.from, to: data.to, fields, finite: true })
    } else {
      addDateToCaptionFields({ ...parameters, fields, finite: true })
    }
    return Object.keys(fields).length ? <Caption fields={fields} /> : ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA || !data?.length) {
      return <div>No records found</div>
    }
    return (
      <div>
        {data.length} row{data.length === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default DoorEventsSummary
